import React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { Button } from '../../components-v2/Button';
import { WarningIcon } from '../svgs/icons/WarningIcon';
import { brandPrimary, background, titles } from '../../styles/colors';

interface DraftErrorBoundaryState {
	hasError: boolean;
	error?: Error;
	errorInfo?: React.ErrorInfo;
}

interface DraftErrorBoundaryProps {
	children: React.ReactNode;
	onRetry?: () => void;
}

export class DraftErrorBoundary extends React.Component<DraftErrorBoundaryProps, DraftErrorBoundaryState> {
	constructor(props: DraftErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(error: Error): DraftErrorBoundaryState {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		console.error('Draft Error Boundary caught an error:', error, errorInfo);
		this.setState({ errorInfo });

		// Log to error reporting service if available
		if (typeof window !== 'undefined' && (window as any).errorReporting) {
			(window as any).errorReporting.captureException(error, {
				extra: errorInfo,
				tags: { component: 'DraftErrorBoundary' },
			});
		}
	}

	handleRetry = () => {
		this.setState({ hasError: false, error: undefined, errorInfo: undefined });
		this.props.onRetry?.();
	};

	render() {
		if (this.state.hasError) {
			return (
				<div className={css(styles.container)}>
					<div className={css(styles.content)}>
						<WarningIcon className={css(styles.icon)} />
						<h2 className={css(styles.title)}>Something went wrong with drafts</h2>
						<p className={css(styles.description)}>
							We encountered an error while loading your draft campaigns. This might be a temporary issue.
						</p>

						{process.env.NODE_ENV === 'development' && this.state.error && (
							<details className={css(styles.errorDetails)}>
								<summary>Error Details (Development only)</summary>
								<pre className={css(styles.errorText)}>
									{this.state.error.toString()}
									{this.state.errorInfo?.componentStack}
								</pre>
							</details>
						)}

						<div className={css(styles.actions)}>
							<Button variant='primary' onClick={this.handleRetry}>
								Try Again
							</Button>
							<Button variant='secondary' onClick={() => window.location.reload()}>
								Refresh Page
							</Button>
						</div>
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}

const styles = StyleSheet.create({
	container: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		minHeight: '400px',
		backgroundColor: background,
		padding: '40px 20px',
	},
	content: {
		textAlign: 'center',
		maxWidth: '500px',
		width: '100%',
	},
	icon: {
		width: '48px',
		height: '48px',
		marginBottom: '16px',
		color: brandPrimary,
	},
	title: {
		fontSize: '24px',
		fontWeight: 'bold',
		color: titles,
		margin: '0 0 12px 0',
	},
	description: {
		fontSize: '16px',
		color: titles,
		margin: '0 0 24px 0',
		lineHeight: '1.5',
	},
	actions: {
		display: 'flex',
		gap: '12px',
		justifyContent: 'center',
		flexWrap: 'wrap',
	},
	errorDetails: {
		textAlign: 'left',
		margin: '20px 0',
		padding: '16px',
		backgroundColor: '#f5f5f5',
		borderRadius: '4px',
		border: '1px solid #ddd',
	},
	errorText: {
		fontSize: '12px',
		fontFamily: 'monospace',
		color: '#666',
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
	},
});
